import cn from 'classnames'
import { useCallback, useMemo } from 'react'

import { Translation } from 'cng-common/features/translations/components'
import { Input } from 'core-components/library/inputs'

import { getNextSortOrderValue } from './helpers'
import { CustomTableColumn, Order } from './models'

import styles from './CustomTableHeaderCell.module.scss'

type BaseProps<T> = {
  column: CustomTableColumn<T>
  isDisabled?: boolean
}

type FilterProps<T> =
  | { filter: string; onFilterChange: (field: keyof T, value: string) => void }
  | { filter?: undefined; onFilterChange?: undefined }

type SortProps<T> =
  | { sortOrder?: Order; onSortOrderChange: (field: keyof T, value: Order) => void }
  | { sortOrder?: undefined; onSortOrderChange?: undefined }

type Props<T> = BaseProps<T> & FilterProps<T> & SortProps<T>

export const CustomTableHeaderCell = <T,>({
  column,
  filter,
  sortOrder,
  onFilterChange,
  onSortOrderChange,
  isDisabled,
}: Props<T>) => {
  const filterLabel = useMemo(
    () => (
      <span>
        Filter by <Translation text={column.columnTitle} span />
      </span>
    ),
    [column.columnTitle]
  )

  const onFilterChangeHandler = useCallback(
    (value: string) => onFilterChange?.(column.columnField, value),
    [column.columnField, onFilterChange]
  )

  const onClickHandler = useCallback(() => {
    if (onSortOrderChange) {
      onSortOrderChange(column.columnField, getNextSortOrderValue(sortOrder ?? ''))
    }
  }, [column.columnField, onSortOrderChange, sortOrder])

  return (
    <div className={styles.cell}>
      <div className={styles.title} onClick={onClickHandler}>
        <Translation text={column.columnTitle} span />
        {sortOrder !== undefined && (
          <div className={cn('kbase kbase-omni-chevron-down', styles.sort, styles[sortOrder])} />
        )}
      </div>
      {filter !== undefined && (
        <Input
          classes={{
            root: cn(styles.filter, { [styles.empty]: !filter }),
          }}
          value={filter}
          label={filterLabel}
          placeholder="Search"
          onChange={onFilterChangeHandler}
          disabled={isDisabled}
        />
      )}
    </div>
  )
}
